"use client";

import React from "react";
import { Building2 } from "lucide-react";

import type { TabContentProps } from "@/components/shared/UniversalDetailPage/types/tab-types";
import type { Contracts } from "@/src/gql/graphql";

import { CompanyDetailsTab } from "@/components/shared/detail-tabs/CompanyDetailsTab";
import { ContractDocumentTab } from "./tabs/ContractDocumentTab";
import { ContractOverviewTab } from "./tabs/ContractOverviewTab";
import {
  ContractCommunicationsDisplay as ContractCommunicationsDisplayBase,
  ContractCompanyContacts,
  ContractContactDisplay as ContractContactDisplayBase,
  ContractDealDisplay as ContractDealDisplayBase,
} from "./tabs/ContractRelatedTabs";

// ============================================================================
// ContractOverviewDisplay — default landing tab (sales summary)
// ============================================================================

export const ContractOverviewDisplay: React.FC<TabContentProps<Contracts>> = ({
  item: contract,
}) => <ContractOverviewTab item={contract} />;

// ============================================================================
// ContractDocumentDisplay — the raw agreement PDF, inline
// ============================================================================

export const ContractDocumentDisplay: React.FC<TabContentProps<Contracts>> = ({
  item: contract,
}) => <ContractDocumentTab item={contract} />;

// ============================================================================
// ContractCompanyDisplay — single parent company (reuses CompanyDetailsTab)
// ============================================================================

export const ContractCompanyDisplay: React.FC<TabContentProps<Contracts>> = ({
  item: contract,
}) => {
  const companyId =
    typeof contract.company_id === "string" && contract.company_id.length > 0
      ? contract.company_id
      : null;

  if (!companyId) {
    return (
      <div className="mt-4 flex flex-col items-center justify-center gap-2 rounded-lg border bg-card p-8 text-center">
        <Building2 className="h-8 w-8 text-muted-foreground" />
        <h3 className="text-base font-semibold text-card-foreground">No company linked</h3>
        <p className="text-sm text-muted-foreground">
          This contract is not linked to a CRM company.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <CompanyDetailsTab companyId={companyId} />
      <ContractCompanyContacts companyId={companyId} />
    </div>
  );
};

// ============================================================================
// ContractContactDisplay / ContractDealDisplay / ContractCommunicationsDisplay
// (thin TabContentProps wrappers over the reusable single-parent displays)
// ============================================================================

export const ContractContactDisplay: React.FC<TabContentProps<Contracts>> = ({
  item: contract,
}) => <ContractContactDisplayBase item={contract} />;

export const ContractDealDisplay: React.FC<TabContentProps<Contracts>> = ({
  item: contract,
}) => <ContractDealDisplayBase item={contract} />;

export const ContractCommunicationsDisplay: React.FC<TabContentProps<Contracts>> = ({
  item: contract,
}) => <ContractCommunicationsDisplayBase item={contract} />;

// ============================================================================
// Namespace export — consumed by useContractDetailModalTabs
// ============================================================================

export const ContractDetailTabComponents = {
  ContractOverviewDisplay,
  ContractDocumentDisplay,
  ContractCompanyDisplay,
  ContractContactDisplay,
  ContractDealDisplay,
  ContractCommunicationsDisplay,
};
